import React, { useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Typography,
  Stack,
  Paper,
  FormControl,
  FormHelperText,
  TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import '../Styles/CategoryStyles.css';
import * as Yup from 'yup';
import { categoryStyles } from '../Styles/CategoryFormStyles';
import { useAtom } from 'jotai';
import { itemSubCategoryAtom } from '../HelperFunctions/atoms';
import {
  CreateItemSubCategoryRequest,
  CreateItemSubCategoryRequestStoreTypeEnum,
} from '../../client-sdk';
import {
  useItemSubCategoryAdd,
  useEditItemSubCategory,
} from '../Query/ItemSubCategoryQuery';
import { primary } from '../../theme';

const ItemSubCategoryForm = ({
  mode,
  onCancel,
  initialData,
}: {
  mode: 'add' | 'edit';
  onCancel: () => void;
  initialData?: {
    id: string;
    englishName: string;
    arabicName: string;
    addedBy: string;
  };
}) => {
  const { mutate } = useItemSubCategoryAdd();
  const { mutate: editItemSubCategory } = useEditItemSubCategory();
  const [loading, setLoading] = useState(false);
  const [categoryId] = useAtom(itemSubCategoryAtom);

  console.log('categoryId', categoryId);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      englishName: initialData?.englishName || '',
      arabicName: initialData?.arabicName || '',
    },
    validationSchema: Yup.object().shape({
      englishName: Yup.string()
        .required('Please enter Category Name')
        .max(100, 'Category Name cannot exceed 100 characters')
        .matches(
          /^[A-Za-z][A-Za-z0-9\s!@#$%^&*(),.?":{}|<>_-]*$/,
          'Category Name must start with a letter and can contain letters, numbers, spaces, and special characters'
        )
        .test(
          'not-only-numbers',
          'Category Name cannot be only numbers',
          (value) => {
            if (!value) return false;
            return !/^\d+$/.test(value.trim());
          }
        )
        .test(
          'not-only-spaces',
          'Category Name cannot be only spaces',
          (value) => {
            if (!value) return false;
            return value.trim().length > 0;
          }
        ),
      arabicName: Yup.string()
        .required('Please enter Category Name in Arabic')
        .max(50, 'Category Name cannot exceed 50 characters')
        .matches(
          /^[\u0600-\u06FF][\u0600-\u06FF\s]*$/,
          'Name must start with an Arabic letter and contain only Arabic letters and spaces'
        )
        .test(
          'not-only-spaces',
          'Name cannot be only spaces',
          (value) => {
            if (typeof value !== 'string') return false;
            return value.trim().length > 0;
          }
        ),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);

        if (!categoryId) {
          console.error('No category data available');
          setLoading(false);
          return;
        }

        const getStoreType = (storeType?: string): CreateItemSubCategoryRequestStoreTypeEnum => {
          if (!storeType) return CreateItemSubCategoryRequestStoreTypeEnum.Restaurant;

          switch (storeType.toLowerCase()) {
            case 'restaurant':
              return CreateItemSubCategoryRequestStoreTypeEnum.Restaurant;
            case 'grocery':
            case 'grocerystore':
              return CreateItemSubCategoryRequestStoreTypeEnum.GroceryStore;
            case 'pharmacy':
            case 'pharmacystore':
              return CreateItemSubCategoryRequestStoreTypeEnum.PharmacyStore;
            default:
              return CreateItemSubCategoryRequestStoreTypeEnum.Restaurant;
          }
        };

        const itemSubCategory: CreateItemSubCategoryRequest = {
          addedBy: 'Admin',
          nameEnglish: values.englishName,
          nameArabic: values.arabicName,
          parentCategoryId: categoryId.id,
          storeType: getStoreType(categoryId.storeType),
          storeId: categoryId.storeId || '',
        };

        const itemEditSubCategory: CreateItemSubCategoryRequest = {
          addedBy: 'Admin',
          nameEnglish: values.englishName,
          nameArabic: values.arabicName,
          parentCategoryId: categoryId.id,
          storeType: getStoreType(categoryId.storeType),
          storeId: categoryId.storeId || '',
        };
        if (mode === 'edit' && initialData?.id) {
          editItemSubCategory(
            { id: initialData?.id, data: itemEditSubCategory },
            {
              onSuccess: () => {
                //   showMessage('Item Category Updated Successfully', 'success');
                formik.resetForm();
                setLoading(false);
                onCancel();
              },
              onError: (error) => {
                console.error('Edit Error:', error);
                setLoading(false);
              },
            }
          );
        } else {
          // This is for "add" mode
          mutate(itemSubCategory, {
            onSuccess: () => {
              formik.resetForm();
              setLoading(false);
              onCancel();
            },
            onError: () => {
              setLoading(false);
            },
          });
        }
      } catch (error) {
        console.error('Error during submission:', error);
        setLoading(false);
      }
    },
  });

  const handleNameChange =
    (field: 'englishName' | 'arabicName') =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (value.length > 50) {
          formik.setFieldError(field, 'Name must not exceed 50 characters');
        } else {
          formik.setFieldValue(field, value);
          formik.setFieldError(field, undefined);
        }
      };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 4,
        mt: 2,
        bgcolor: '#fff',
        borderRadius: 1,
        border: '1px solid #ddd',
      }}
    >
      {/* {loading && (
        <Box sx={categoryStyles.loaderBox}>
          <CircularProgress />
        </Box>
      )} */}

      <Box component="form" noValidate onSubmit={formik.handleSubmit}>
        <Grid
          container
          direction="column"
          spacing={3}
          maxWidth="1200px"
          sx={{ width: '100%' }}
        >
          {/* English Name */}
          <Grid>
            <Box sx={categoryStyles.labelBox}>
              <Typography sx={categoryStyles.labelText}>
                 Sub Category Name (In English)*
              </Typography>
              <FormControl
                fullWidth
                error={
                  formik.touched.englishName &&
                  Boolean(formik.errors.englishName)
                }
                sx={{
                  alignItems: 'flex-start',
                  '& .MuiInput-root': {
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: '2px solid rgba(0, 0, 0, 0.42)',
                    },
                    '&:before': {
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                    },
                  },
                }}
              >
                <TextField
                  placeholder="Enter English category name"
                  name="englishName"
                  value={formik.values.englishName}
                  onChange={handleNameChange('englishName')}
                  onBlur={formik.handleBlur}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: 14,
                      color: 'text.primary',
                      width: '400px',
                      height: '45px',
                      marginBottom: '8px',
                    },
                    '& .MuiFormLabel-root': {
                      fontSize: 14,
                    },
                  }}
                />
                {formik.touched.englishName && formik.errors.englishName && (
                  <FormHelperText
                    error
                    sx={{ ...categoryStyles.errorText, mt: 2}}
                  >
                    {formik.errors.englishName}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>
          </Grid>

          {/* Arabic Name */}
          <Grid>
            <Box sx={categoryStyles.labelBox}>
              <Typography sx={categoryStyles.labelText}>
                Sub Category Name (In Arabic)*
              </Typography>
              <FormControl
                fullWidth
                error={
                  formik.touched.arabicName && Boolean(formik.errors.arabicName)
                }
                sx={{
                  alignItems: 'flex-start',
                  '& .MuiInput-root': {
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    '&:hover:not(.Mui-disabled):before': {
                      borderBottom: '2px solid rgba(0, 0, 0, 0.42)',
                    },
                    '&:before': {
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                    },
                  },
                }}
              >
                <TextField
                  placeholder="أدخل اسم الفئة بالعربية"
                  name="arabicName"
                  value={formik.values.arabicName}
                  onChange={handleNameChange('arabicName')}
                  onBlur={formik.handleBlur}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: 14,
                      color: 'text.primary',
                      width: '400px',
                      height: '45px',
                       marginBottom: '8px',
                    },
                    '& .MuiFormLabel-root': {
                      fontSize: 14,
                    },
                  }}
                />
                {formik.touched.arabicName && formik.errors.arabicName && (
                  <FormHelperText
                    error
                    sx={{ ...categoryStyles.errorText, mt: 2 }}
                  >
                    {formik.errors.arabicName}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>
          </Grid>

          {/* Buttons */}
          <Grid>
            <Box sx={categoryStyles.buttonsWrapper}>
              <Box sx={{ minWidth: { xs: '100%', sm: '250px' } }} />
              <Stack
                direction="row"
                spacing={2}
                justifyContent="flex-start"
                sx={{ width: '100%' }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ bgcolor: primary.main }}
                  disabled={
                    mode === 'edit' &&
                    (!formik.dirty || !formik.isValid || loading)
                  }
                >
                  {loading ? 'Saving...' : mode === 'edit' ? 'Update' : 'Save'}
                </Button>
                <Button
                  variant="contained"
                  sx={categoryStyles.cancelButton}
                  onClick={() => {
                    formik.resetForm();
                    onCancel();
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default ItemSubCategoryForm;
